import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Download, Upload, FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

const downloadBlob = async (url, fallbackName) => {
  const res = await api.get(url, { responseType: "blob" });
  const name = res.headers["content-disposition"]?.match(/filename="?([^"]+)"?/)?.[1] || fallbackName;
  const href = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = href; a.download = name; a.click();
  URL.revokeObjectURL(href);
};

export const ExcelTools = ({ kind, label, onImported }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const exportXlsx = async () => {
    try { await downloadBlob(`/export/${kind}`, `${kind}.xlsx`); toast.success("Excel indirildi"); }
    catch { toast.error("İndirilemedi"); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true); setResult(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/import/${kind}`, fd);
      setResult(data);
      toast.success(`${data.inserted} yeni, ${data.updated} güncellendi`);
      onImported?.();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Yükleme başarısız");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={exportXlsx} data-testid={`export-${kind}-btn`}>
        <Download size={16} className="mr-1" /> Excel İndir
      </Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setResult(null); }}>
        <DialogTrigger asChild>
          <Button variant="outline" data-testid={`import-${kind}-btn`}><Upload size={16} className="mr-1" /> Excel Yükle</Button>
        </DialogTrigger>
        <DialogContent className="bg-popover max-w-md">
          <DialogHeader><DialogTitle>{label} — Excel'den Yükle</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-muted-foreground">
              Şablonu indirip doldurun, ardından .xlsx dosyasını yükleyin. Aynı isimdeki kayıtlar güncellenir, yenileri eklenir.
            </p>
            <Button variant="secondary" size="sm" onClick={() => downloadBlob(`/import/${kind}/template`, `${kind}_sablon.xlsx`)} data-testid={`template-${kind}-btn`}>
              <FileSpreadsheet size={14} className="mr-1" /> Şablon İndir
            </Button>
            <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-md p-6 cursor-pointer hover:border-primary transition-colors">
              {busy ? <Loader2 className="animate-spin" /> : <Upload className="text-muted-foreground" />}
              <span className="text-muted-foreground">{busy ? "Yükleniyor..." : "Dosya seçmek için tıklayın (.xlsx)"}</span>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid={`import-${kind}-file`} />
            </label>
            {result && (
              <div className="rounded-md bg-muted/50 p-3 space-y-1" data-testid={`import-${kind}-result`}>
                <div><span className="font-semibold text-emerald-500">{result.inserted}</span> yeni kayıt eklendi</div>
                <div><span className="font-semibold text-primary">{result.updated}</span> kayıt güncellendi</div>
                {result.errors?.length > 0 && (
                  <ul className="text-xs text-amber-500 list-disc pl-4 pt-1">{result.errors.map((er, i) => <li key={i}>{er}</li>)}</ul>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
